import type { AiGeneratedRisk } from '../types';

interface AiGeneratedRisksTableProps {
  risks: AiGeneratedRisk[];
  loading: boolean;
  error: string | null;
}

function valueOrDash(value: string | null | undefined): string {
  return value?.trim() || '—';
}

function compareAiRiskCodes(leftCode: string, rightCode: string): number {
  const left = leftCode.trim().toUpperCase();
  const right = rightCode.trim().toUpperCase();
  const leftSuffix = left.startsWith('R') ? left.slice(1) : left;
  const rightSuffix = right.startsWith('R') ? right.slice(1) : right;

  return (
    leftSuffix.length - rightSuffix.length ||
    leftSuffix.localeCompare(rightSuffix)
  );
}

/**
 * AI Generated Risks — restyled as scenario cards to read coherently next
 * to the Risk Scenario Catalogs above it (same visual language: a code
 * chip, a title, and labeled columns), even though the two are unrelated
 * data sources and this table's fields are unchanged (still
 * strideCategory/affectedAsset/attackPath/evidence/feasibleScenario/
 * mitigatingGuidance, sourced from AgentCore evaluation output, not from
 * Confluence).
 */
export function AiGeneratedRisksTable({
  risks,
  loading,
  error,
}: AiGeneratedRisksTableProps) {
  const orderedRisks = [...risks].sort((left, right) =>
    compareAiRiskCodes(left.code, right.code),
  );

  return (
    <section
      className="risk-library-card"
      aria-labelledby="ai-generated-risks-title"
    >
      <div className="risk-catalogue-panel-heading">
        <div>
          <span className="risk-catalogue-section-label">
            Sourced from AgentCore evaluations
          </span>
          <h2 id="ai-generated-risks-title">AI Generated Risks</h2>
          <p>
            Read-only risks captured from AgentCore evaluations. Official Risk
            Scenario Catalogs remain a separate, Confluence-sourced reference
            from this generated-risk store.
          </p>
        </div>
        <span className="risk-catalogue-count">
          {risks.length} risk{risks.length === 1 ? '' : 's'}
        </span>
      </div>

      {error && <p className="risk-library-error">{error}</p>}
      {loading && <p className="loading-text">Loading AI-generated risks…</p>}

      {!loading && risks.length === 0 && !error && (
        <p className="risk-catalogue-empty">
          No AI-generated risks are available yet. AgentCore evaluation output
          will appear here when it is captured.
        </p>
      )}

      {!loading && risks.length > 0 && (
        <div className="risk-scenario-list">
          {orderedRisks.map(risk => (
            <article className="risk-scenario-card" key={risk.aiRiskId}>
              <header className="risk-scenario-card-header">
                <div className="risk-scenario-heading">
                  <span className="risk-scenario-code">
                    {valueOrDash(risk.code)}
                  </span>
                  <h4>{valueOrDash(risk.description)}</h4>
                </div>
                <small>
                  {valueOrDash(risk.applicationName || risk.ban)} &middot;{' '}
                  {risk.ban}
                </small>
              </header>
              <div className="risk-scenario-grid">
                <section className="risk-scenario-column">
                  <h5>STRIDE / affected asset</h5>
                  <p>{valueOrDash(risk.strideCategory)}</p>
                  <p>{valueOrDash(risk.affectedAsset)}</p>
                </section>
                <section className="risk-scenario-column">
                  <h5>Attack path</h5>
                  <p>{valueOrDash(risk.attackPath)}</p>
                </section>
                <section className="risk-scenario-column">
                  <h5>Evidence</h5>
                  <p>{valueOrDash(risk.evidence)}</p>
                </section>
              </div>
              <div className="risk-scenario-grid">
                <section className="risk-scenario-column">
                  <h5>Feasible scenario</h5>
                  <p>{valueOrDash(risk.feasibleScenario)}</p>
                </section>
                <section className="risk-scenario-column">
                  <h5>Mitigating guidance</h5>
                  <p>{valueOrDash(risk.mitigatingGuidance)}</p>
                </section>
                <section className="risk-scenario-column">
                  <h5>Source evaluation</h5>
                  <p>{valueOrDash(risk.sourceEvaluationId)}</p>
                </section>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
